import { OBJ } from '../constants';
import { makeImage, makeShape, makeText } from './objects';
import { markRange } from './richText';
import { wrapTextLines } from './drawObjects';

/**
 * The things Word puts on a page that this editor had no way to put on one:
 * a cover page, a chart, line numbers down the margin, a drop cap, and the
 * fields (file name, page count) that used to be typed by hand.
 *
 * Everything here returns ordinary objects, so each one stays movable,
 * restylable and deletable once it lands — there is no new kind of thing on
 * the page that the rest of the editor does not understand.
 */

const MARGIN = 56;

/* ------------------------------------------------------------- cover page */

export function buildCoverPage(page, { title, subtitle, author, date }, settings) {
    const width = page.width - MARGIN * 2;
    const items = [];
    let y = page.height * 0.32;

    const push = (text, size, { bold = false, gap = 18, color = '#1C1815' } = {}) => {
        if (!text) return;
        const height = size * 1.35 + 6;
        items.push(makeText(
            { x: MARGIN, y, w: width, h: height },
            { ...settings, fontSize: size, bold, align: 'center', color },
            text,
        ));
        y += height + gap;
    };

    push(title, 30, { bold: true });
    push(subtitle, 16, { color: '#6B635C' });
    // The by-line and the date sit together, away from the title block, which
    // is how a title page is set whatever it is a title page for.
    y = page.height * 0.72;
    push(author, 13);
    push(date, 12, { color: '#6B635C' });

    // A rule under the title block, for the same reason a title page has one.
    items.push(makeShape(OBJ.LINE, {
        x: page.width / 2 - 70,
        y: page.height * 0.32 + 30 * 1.35 + 26,
        w: 140,
        h: 0,
    }, { stroke: '#C24A2E', strokeWidth: 1.5, fill: 'transparent' }));

    return items;
}

/* ------------------------------------------------------------------ chart */

const CHART_COLORS = ['#C24A2E', '#1E5FA8', '#1E7A4B', '#8A6A2F', '#6D43C8', '#B7791F'];

/**
 * A chart, drawn once into a picture.
 *
 * A chart is not editable data on a PDF page — it is a picture of some numbers
 * — so this draws it at export resolution and inserts the result as an image,
 * which every part of the editor already knows how to move, scale and export.
 */
export function buildChart(page, { kind, labels, values }, scale = 3) {
    const width = Math.min(page.width - MARGIN * 2, 420);
    const height = Math.round(width * 0.62);
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);

    const max = Math.max(...values, 1);
    const pad = 28;
    ctx.font = '9px system-ui, sans-serif';
    ctx.textAlign = 'center';

    if (kind === 'pie') {
        const total = values.reduce((a, b) => a + b, 0) || 1;
        const radius = Math.min(width, height) / 2 - pad;
        const cx = width / 2;
        const cy = height / 2;
        let start = -Math.PI / 2;
        values.forEach((value, i) => {
            const slice = (value / total) * Math.PI * 2;
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.arc(cx, cy, radius, start, start + slice);
            ctx.closePath();
            ctx.fillStyle = CHART_COLORS[i % CHART_COLORS.length];
            ctx.fill();
            // The label rides on the slice it belongs to, so a legend is not
            // another thing to keep in step with the numbers.
            const mid = start + slice / 2;
            ctx.fillStyle = '#FFFFFF';
            ctx.fillText(labels[i] || '', cx + Math.cos(mid) * radius * 0.62, cy + Math.sin(mid) * radius * 0.62 + 3);
            start += slice;
        });
    } else {
        const plotW = width - pad * 2;
        const plotH = height - pad * 2;
        const step = plotW / values.length;

        ctx.strokeStyle = '#E4E0DA';
        ctx.lineWidth = 1;
        for (let i = 0; i <= 4; i += 1) {
            const y = pad + (plotH / 4) * i;
            ctx.beginPath();
            ctx.moveTo(pad, y);
            ctx.lineTo(pad + plotW, y);
            ctx.stroke();
        }

        values.forEach((value, i) => {
            const barH = (value / max) * plotH;
            const x = pad + step * i + step * 0.2;
            const w = step * 0.6;
            if (kind === 'line') {
                if (i === 0) { ctx.beginPath(); ctx.moveTo(x + w / 2, pad + plotH - barH); }
                else ctx.lineTo(x + w / 2, pad + plotH - barH);
            } else {
                ctx.fillStyle = CHART_COLORS[i % CHART_COLORS.length];
                ctx.fillRect(x, pad + plotH - barH, w, barH);
            }
            ctx.fillStyle = '#6B635C';
            ctx.fillText(labels[i] || '', x + w / 2, height - pad + 12);
        });

        if (kind === 'line') {
            ctx.strokeStyle = CHART_COLORS[1];
            ctx.lineWidth = 2;
            ctx.stroke();
        }
    }

    return makeImage(
        {
            x: (page.width - width) / 2,
            y: Math.max(MARGIN, page.height * 0.3),
            w: width,
            h: height,
        },
        canvas.toDataURL('image/png'),
        canvas.width,
        canvas.height,
    );
}

/* ----------------------------------------------------------- line numbers */

/**
 * Word numbers the lines of its own text; this numbers the lines of the PDF's,
 * which is what a reviewer quoting a contract back at someone needs. The line
 * positions come from the text layer, so a page with no text gets nothing.
 */
export function buildLineNumbers(page, lines, settings) {
    return lines.map((line, index) => makeText(
        { x: Math.max(4, line.x - 34), y: line.y - 2, w: 26, h: 12 },
        { ...settings, fontSize: 8, align: 'right', color: '#A8A099' },
        String(index + 1),
    ));
}

/* --------------------------------------------------------------- drop cap */

/** The first letter, set large — as a run style, so the text stays one box. */
export function applyDropCap(obj, on) {
    const text = obj.text || '';
    if (!text.trim()) return null;
    if (!on) {
        const marks = (obj.marks || []).filter((mark) => !(mark.start === 0 && mark.end === 1));
        return { marks: marks.length ? marks : null };
    }
    return {
        marks: markRange(obj, 0, 1, {
            fontSize: Math.round((obj.fontSize || 14) * 2.4),
            bold: true,
        }),
    };
}

/* ---------------------------------------------------------------- fields */

/** Word's fields, as the four a PDF can actually answer. */
export function fieldValue(kind, { fileName, pageCount, pageIndex }) {
    switch (kind) {
        case 'fileName': return (fileName || '').replace(/\.pdf$/i, '');
        case 'pageCount': return String(pageCount);
        case 'pageOf': return `${pageIndex + 1} / ${pageCount}`;
        case 'date': return new Date().toLocaleDateString();
        default: return '';
    }
}

/* --------------------------------------------------- table of contents */

/* -------------------------------------------------------------- columns */

/**
 * Word's Columns, as a PDF can hold them: one text box becomes several,
 * side by side, with the text poured through them.
 *
 * A PDF has no flowing body text to reflow, so the split is done once, here —
 * the text is re-wrapped at the column width and dealt out line by line, which
 * is what the reader will see and what the exporter will write.
 *
 * @returns {object[]|null} the new boxes, or null if there was nothing to split
 */
export function splitIntoColumns(obj, count, gutter = 16) {
    if (!obj || obj.type !== OBJ.TEXT || count < 2) return null;
    const text = (obj.text || '').trim();
    if (!text) return null;

    const columnWidth = (obj.w - gutter * (count - 1)) / count;
    if (columnWidth < 40) return null;

    // Re-wrapped at the width the text is about to be set in, not the width it
    // is in now — otherwise the lines dealt out are the wrong lines.
    const lines = wrapTextLines({ ...obj, w: columnWidth })
        .map((line) => line.text)
        .filter((line, i, all) => line.trim() || (i && i < all.length - 1));
    if (!lines.length) return null;

    const perColumn = Math.ceil(lines.length / count);
    const boxes = [];
    for (let i = 0; i < count; i += 1) {
        const slice = lines.slice(i * perColumn, (i + 1) * perColumn);
        if (!slice.length) continue;
        boxes.push(makeText(
            {
                x: obj.x + i * (columnWidth + gutter),
                y: obj.y,
                w: columnWidth,
                h: obj.h,
            },
            obj,
            slice.join('\n'),
        ));
    }
    return boxes.length > 1 ? boxes : null;
}
